import { Box, Center, Flex, Spinner, Text } from '@chakra-ui/react'
import { useTranslation } from '@infraloop/i18n'
import { UserProfileForAdmin } from '@infraloop/services'

import { USER_LIST_COLUMN_GAP, USER_LIST_GRID_COLUMNS } from '../layout'
import { SortDirection, SortState } from '../utils'
import UserRow from './UserRow'

const HEADER_CELL_PROPS = {
  fontSize: '11px',
  fontWeight: 'bold',
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  color: 'gray.500',
} as const

/** Glyph shown next to the subscription-end header for each sort state. */
const SORT_INDICATOR = {
  [SortDirection.Desc]: '↓',
  [SortDirection.Asc]: '↑',
  none: '↕',
} as const

const UserListTable = ({
  users,
  editMode,
  changedHubspotIds,
  sortDir,
  isLoading,
  isFetching,
  onToggleSort,
  onHubspotIdChange,
  onClearHubspotId,
}: {
  /** Users on the current page. */
  users: UserProfileForAdmin[]
  /** Whether HubSpot-ID edit mode is active. */
  editMode: boolean
  /** Pending HubSpot-ID edits, keyed by user id. */
  changedHubspotIds: Record<string, string>
  /** Current sort state of the subscription-end column (`undefined` when unsorted). */
  sortDir: SortState
  /** First load (no data yet) — shows a centered spinner in place of rows. */
  isLoading: boolean
  /** Any fetch in flight — overlays a spinner over the current rows. */
  isFetching: boolean
  /** Step the subscription-end sort to its next state (descending, ascending, unsorted). */
  onToggleSort: () => void
  /** Update a row's pending HubSpot ID. */
  onHubspotIdChange: (userId: string, value: string) => void
  /** Discard a row's pending HubSpot-ID change. */
  onClearHubspotId: (userId: string) => void
}) => {
  const { t } = useTranslation()

  return (
    <Box
      position="relative"
      mt="16px"
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="14px"
      boxShadow="0 1px 2px rgba(16,24,40,.04), 0 1px 3px rgba(16,24,40,.06)"
    >
      {/* Header */}
      <Box
        display="grid"
        gridTemplateColumns={USER_LIST_GRID_COLUMNS}
        alignItems="center"
        columnGap={USER_LIST_COLUMN_GAP}
        px="18px"
        py="13px"
        bg="#FBFCFE"
        borderTopRadius="14px"
        borderBottom="1px solid"
        borderColor="gray.200"
      >
        <Text {...HEADER_CELL_PROPS}>{t('hatsuly.userList.columns.user')}</Text>
        <Text {...HEADER_CELL_PROPS}>{t('hatsuly.userList.columns.hubspot')}</Text>
        <Flex
          {...HEADER_CELL_PROPS}
          as="button"
          align="center"
          gap="5px"
          cursor="pointer"
          userSelect="none"
          _hover={{ color: 'gray.700' }}
          onClick={onToggleSort}
          title={t(sortDir ? 'hatsuly.userList.sortNext' : 'hatsuly.userList.sortUnsorted')}
          data-testid="user-list-sort"
        >
          {t('hatsuly.userList.columns.subscriptionEnd')}
          <Box as="span" color={sortDir ? 'primary.600' : 'gray.400'} fontSize="12px">
            {SORT_INDICATOR[sortDir ?? 'none']}
          </Box>
        </Flex>
        <Text {...HEADER_CELL_PROPS}>{t('hatsuly.userList.columns.billing')}</Text>
      </Box>

      {/* Body */}
      {isLoading && (
        <Center py="40px">
          <Spinner color="primary.500" />
        </Center>
      )}
      {!isLoading && users.length === 0 && (
        <Box py="30px" px="18px" textAlign="center" color="gray.500" fontSize="14px">
          {t('hatsuly.userList.empty')}
        </Box>
      )}
      {!isLoading &&
        users.map((user) => (
          <UserRow
            key={user.user_id}
            user={user}
            editMode={editMode}
            changedHubspotId={user.user_id in changedHubspotIds ? changedHubspotIds[user.user_id] : undefined}
            onHubspotIdChange={onHubspotIdChange}
            onClearHubspotId={onClearHubspotId}
          />
        ))}

      {/* Background refetch (paging/filter/search): keep rows in place under a spinner overlay. */}
      {isFetching && !isLoading && (
        <Center
          position="absolute"
          inset={0}
          borderRadius="14px"
          bg="rgba(255,255,255,.6)"
          zIndex={1}
          data-testid="user-list-loading"
        >
          <Spinner color="primary.500" />
        </Center>
      )}
    </Box>
  )
}

export default UserListTable
